/**
 * i18n bootstrap — initialises i18next with react-i18next.
 *
 * Architecture:
 *  - Namespaces are organised by domain/feature concern (see below).
 *  - ALL user-visible strings must live in a locale file, never in JSX.
 *  - Adding a language = add a new locale bundle and register it here.
 *
 * Namespace map:
 *  common        Generic labels, actions, errors shared across the whole app
 *  auth          Login, register, password-reset screens
 *  dashboard     Dashboard KPIs, activity feed
 *  patients      Patient list, chart, registration
 *  appointments  Appointment booking, schedule, queue
 *  billing       Invoices, payments
 *  reports       Reports & analytics
 *  staff         Staff management, roles & permissions
 *  settings      Clinic profile, services, reminders, branding
 *  subscription  Plans & billing portal
 *  portal        Patient portal
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// ─── Locale bundles ─────────────────────────────────────────
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enPatients from './locales/en/patients.json';
import enAppointments from './locales/en/appointments.json';
import enBilling from './locales/en/billing.json';
import enReports from './locales/en/reports.json';
import enStaff from './locales/en/staff.json';
import enSettings from './locales/en/settings.json';
import enSubscription from './locales/en/subscription.json';
import enPortal from './locales/en/portal.json';
import enLanding from './locales/en/landing.json';
import enOnboarding from './locales/en/onboarding.json';
import enClinicSetup from './locales/en/clinicSetup.json';
import enAdmin from './locales/en/admin.json';
import enNotifications from './locales/en/notifications.json';
import enAudit from './locales/en/audit.json';

import hiDashboard from './locales/hi/dashboard.json';
import hinDashboard from './locales/hin/dashboard.json';

export const DEFAULT_LANGUAGE = 'en';
export const SUPPORTED_LANGUAGES = ['en', 'hi', 'hin'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const NAMESPACES = [
  'common',
  'auth',
  'dashboard',
  'patients',
  'appointments',
  'billing',
  'reports',
  'staff',
  'settings',
  'subscription',
  'portal',
  'landing',
  'onboarding',
  'clinicSetup',
  'admin',
  'notifications',
  'audit',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

i18n
  .use(initReactI18next)
  .init({
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: 'common',
    ns: NAMESPACES,

    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        dashboard: enDashboard,
        patients: enPatients,
        appointments: enAppointments,
        billing: enBilling,
        reports: enReports,
        staff: enStaff,
        settings: enSettings,
        subscription: enSubscription,
        portal: enPortal,
        landing: enLanding,
        onboarding: enOnboarding,
        clinicSetup: enClinicSetup,
        admin: enAdmin,
        notifications: enNotifications,
        audit: enAudit,
      },
      hi: {
        dashboard: hiDashboard,
      },
      hin: {
        dashboard: hinDashboard,
      },
    },

    interpolation: {
      escapeValue: false, // React already handles XSS
    },

    react: {
      useSuspense: true,
    },
  });

export default i18n;

// Re-export the React i18n hooks so consumers get every i18n concern
// (init side-effect, namespaces, hooks, types) from this single package.
export { useTranslation, Trans } from 'react-i18next';
